import { levelFor, type ConfidenceScore } from './confidence-score'
import type { AirlineWaiverFee } from './types'

/**
 * An airline waiver registered by ops (SPEC.md §10.7). When an agent enters its code on a quote and
 * every condition is met, the quote scores at least `score`, and a waiver with `airlineFeePerTicket`
 * also sets the fee the airline keeps on each ticket. Dates are inclusive `YYYY-MM-DD`; an empty
 * list or null means "any".
 */
export interface RefundWaiver {
  code: string
  /** Two-letter airline code. */
  carrier: string
  /** Ticketing PCCs the waiver covers. */
  pccs: string[]
  /** The PLT market the booking was sold in: AU, NZ, US or CA. */
  market: string | null
  saleFrom: string | null
  saleTo: string | null
  ticketingFrom: string | null
  ticketingTo: string | null
  /** The cancellation must be at least this many days before departure. */
  minDaysBeforeDeparture: number | null
  codeshareAllowed: boolean
  score: number
  /**
   * The fee the airline keeps on each ticket under the waiver, in the booking currency (FJ PPAZ04926:
   * 100). Null leaves the amounts to the fare rules and Sabre, and the waiver changes only the score.
   */
  airlineFeePerTicket: number | null
}

/** What the quote knows about the booking, gathered by the Refunds screen. */
export interface WaiverBookingFacts {
  carrier?: string
  ticketingPcc?: string
  currency?: string
  bookedAt?: string
  ticketedAt?: string
  departureDate?: string
  now: Date
  /** The operating carrier is not in the data, so an agent confirms there is no codeshare segment. */
  agentConfirmedNoCodeshare: boolean
}

export type WaiverCheckState = 'met' | 'not-met' | 'unknown' | 'agent-to-confirm'

export interface WaiverCheck {
  condition: string
  state: WaiverCheckState
  detail: string
}

export interface WaiverEvaluation {
  applies: boolean
  checks: WaiverCheck[]
}

const MARKET_BY_CURRENCY: Record<string, string> = { AUD: 'AU', NZD: 'NZ', USD: 'US', CAD: 'CA' }

/** PLT sells each market in its own currency, so the booking currency names the market it was sold in. */
export function marketForCurrency(currency: string | undefined): string | undefined {
  return currency ? MARKET_BY_CURRENCY[currency.toUpperCase()] : undefined
}

const dayOf = (value: string | undefined): string | undefined =>
  value && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : undefined

const MS_PER_DAY = 86_400_000

function daysBetween(from: Date, toDay: string): number {
  const start = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate())
  return Math.floor((Date.parse(`${toDay}T00:00:00Z`) - start) / MS_PER_DAY)
}

function periodCheck(
  condition: string,
  day: string | undefined,
  from: string | null,
  to: string | null,
  missing: string,
): WaiverCheck | undefined {
  if (!from && !to) return undefined
  if (!day) return { condition, state: 'unknown', detail: missing }
  const inside = (!from || day >= from) && (!to || day <= to)
  return { condition, state: inside ? 'met' : 'not-met', detail: day }
}

/** Every condition, each with its own state, so the agent sees exactly which one stops the waiver. */
export function evaluateWaiver(waiver: RefundWaiver, facts: WaiverBookingFacts): WaiverEvaluation {
  const checks: WaiverCheck[] = []

  const carrier = facts.carrier?.toUpperCase()
  checks.push(
    carrier
      ? {
          condition: `Carrier is ${waiver.carrier}`,
          state: carrier === waiver.carrier ? 'met' : 'not-met',
          detail: carrier,
        }
      : { condition: `Carrier is ${waiver.carrier}`, state: 'unknown', detail: 'The quote has no carrier' },
  )

  if (waiver.pccs.length > 0) {
    const pcc = facts.ticketingPcc?.toUpperCase()
    checks.push(
      pcc
        ? {
            condition: `Ticketed on ${waiver.pccs.join(' or ')}`,
            state: waiver.pccs.includes(pcc) ? 'met' : 'not-met',
            detail: pcc,
          }
        : {
            condition: `Ticketed on ${waiver.pccs.join(' or ')}`,
            state: 'unknown',
            detail: 'The quote has no ticketing PCC',
          },
    )
  }

  if (waiver.market) {
    const market = marketForCurrency(facts.currency)
    checks.push(
      market
        ? { condition: `Sold in ${waiver.market}`, state: market === waiver.market ? 'met' : 'not-met', detail: market }
        : { condition: `Sold in ${waiver.market}`, state: 'unknown', detail: 'The booking currency names no market' },
    )
  }

  const sale = periodCheck(
    'Booked within the sales period',
    dayOf(facts.bookedAt),
    waiver.saleFrom,
    waiver.saleTo,
    'The booking date is not known',
  )
  if (sale) checks.push(sale)

  const ticketing = periodCheck(
    'Ticketed within the ticketing period',
    dayOf(facts.ticketedAt),
    waiver.ticketingFrom,
    waiver.ticketingTo,
    'The ticket issue date is not known',
  )
  if (ticketing) checks.push(ticketing)

  if (waiver.minDaysBeforeDeparture != null) {
    const condition = `At least ${waiver.minDaysBeforeDeparture} days before departure`
    const departure = dayOf(facts.departureDate)
    if (!departure) {
      checks.push({ condition, state: 'unknown', detail: 'The departure date is not known' })
    } else {
      const days = daysBetween(facts.now, departure)
      checks.push({
        condition,
        state: days >= waiver.minDaysBeforeDeparture ? 'met' : 'not-met',
        detail: `${days} days`,
      })
    }
  }

  if (!waiver.codeshareAllowed) {
    checks.push({
      condition: 'No codeshare segments',
      state: facts.agentConfirmedNoCodeshare ? 'met' : 'agent-to-confirm',
      detail: facts.agentConfirmedNoCodeshare ? 'Confirmed by the agent' : 'The operating carrier is not in the data',
    })
  }

  return { applies: checks.every(check => check.state === 'met'), checks }
}

/** The fee the calculator charges on each ticket, when the waiver applies and sets one. */
export function waiverFeeFor(
  waiver: RefundWaiver | null | undefined,
  evaluation: WaiverEvaluation | null | undefined,
): AirlineWaiverFee | undefined {
  if (!waiver || !evaluation?.applies || waiver.airlineFeePerTicket == null) return undefined
  return { code: waiver.code, perTicket: waiver.airlineFeePerTicket }
}

/**
 * Lifts the score to the waiver's when it applies. A quote with no automated figure stays at 0: a
 * waiver without a fee says the airline will honour a refund, not what the refund is. One with a fee
 * gives every ticket a figure (AIRLINE_WAIVER), so it never reaches this as UNRESOLVED.
 */
export function applyWaiverToConfidence(
  confidence: ConfidenceScore,
  waiver: RefundWaiver,
  evaluation: WaiverEvaluation,
): ConfidenceScore {
  // A waiver settles the airline's fee, not what was paid the supplier, so the cost basis still caps it.
  const score = Math.min(waiver.score, confidence.costBasisScore ?? 100, confidence.uncertaintyCap ?? 100)
  if (!evaluation.applies || confidence.source === 'UNRESOLVED' || score <= confidence.score) {
    return confidence
  }

  const note = `Waiver ${waiver.code}: every condition is met.`
  return {
    ...confidence,
    score,
    level: levelFor(score),
    factors: [...confidence.factors, note],
    breakdown: [...confidence.breakdown, { label: note, points: score - confidence.score }],
  }
}
